// components
import RecurrenceRulesSection from './components/RecurrenceRulesSection/RecurrenceRulesSection';
import { Icon } from '@sdb/planning-ui';
import { IconButton, Typography } from '@mui/material';

// hooks/utils
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect, useState } from 'react';
import schema from './RecurrencePatternBuilder.schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';

// types
import {
  DayIndex,
  RecurrencePattern,
  RecurrencePatternBuilderProps
} from './RecurrencePatternBuilder.types';
import dayjs from 'dayjs';

// styles
import { RecurrenceRepresentationRow } from './RecurrencePatternBuilder.styles';

const RecurrencePatternBuilder = ({
  date,
  isExistingAppointment,
  allowedToEditRecurrenceRule,
  pattern,
  minPatternEnd,
  onPatternChange,
  recurrenceFieldRef
}: RecurrencePatternBuilderProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'mrp.appointmentForm' });
  const [isEditingRecurrence, setIsEditingRecurrence] = useState(false);

  const methods = useForm<RecurrencePattern>({
    defaultValues: schema.cast(pattern),
    resolver: yupResolver(schema)
  });

  const { getValues, control, reset, setValue } = methods;

  const [
    recurrenceRule,
    daysSelected,
    recurrenceInterval,
    recurrenceCount,
    recurrenceEnd,
    recurrenceRangeType
  ] = useWatch({
    control,
    name: [
      'recurrenceRule',
      'daysSelected',
      'recurrenceInterval',
      'recurrenceCount',
      'recurrenceEnd',
      'recurrenceRangeType'
    ]
  });

  useEffect(() => {
    const currentValues = getValues();
    if (isEqual(currentValues, pattern)) {
      return;
    }

    setIsEditingRecurrence(false);
    reset(pattern);
  }, [getValues, pattern, reset]);

  useEffect(() => {
    const selectedDate = dayjs(date);
    const weekDay = (selectedDate.isoWeekday() - 1) as DayIndex;

    if (daysSelected?.includes(weekDay)) return;

    setValue('daysSelected', [weekDay]);
  }, [date, daysSelected, pattern, setValue]);

  useEffect(() => {
    onPatternChange(getValues());
  }, [
    recurrenceRule,
    daysSelected,
    recurrenceInterval,
    recurrenceCount,
    recurrenceEnd,
    recurrenceRangeType,
    onPatternChange,
    getValues
  ]);

  if (
    isExistingAppointment &&
    recurrenceRule === 'none' &&
    !allowedToEditRecurrenceRule
  ) {
    return;
  }

  const editRecurrenceButton = (
    <IconButton size="small" onClick={() => setIsEditingRecurrence(true)}>
      <Icon type="pen" fontSize="small" />
    </IconButton>
  );

  const newAppointmentSection = (
    <RecurrenceRulesSection
      minPatternEnd={minPatternEnd}
      recurrenceFieldRef={recurrenceFieldRef}
    />
  );

  const addRecurrenceRow = (
    <RecurrenceRepresentationRow>
      <Typography variant="body2">{t('addRecurrencePattern')}</Typography>
      <IconButton
        size="small"
        onClick={() => {
          setValue('recurrenceRule', 'weekly');
          setIsEditingRecurrence(true);
        }}
      >
        <Icon type="pen" fontSize="small" />
      </IconButton>
    </RecurrenceRepresentationRow>
  );

  const showRecurrenceRow = (
    <RecurrenceRepresentationRow>
      {getValues('recurrenceRuleRepresentation')}
      {allowedToEditRecurrenceRule && editRecurrenceButton}
    </RecurrenceRepresentationRow>
  );

  const recurrenceRulesSection = (
    <RecurrenceRulesSection
      minPatternEnd={minPatternEnd}
      recurrenceFieldRef={recurrenceFieldRef}
      hideWeekDayPicker={recurrenceRule !== 'none'}
      disableRecurrenceRule={recurrenceRule !== 'none'}
    />
  );

  return (
    <FormProvider {...methods}>
      {!isExistingAppointment && newAppointmentSection}
      {isExistingAppointment && isEditingRecurrence && recurrenceRulesSection}
      {isExistingAppointment &&
        !isEditingRecurrence &&
        recurrenceRule === 'none' &&
        addRecurrenceRow}
      {isExistingAppointment &&
        !isEditingRecurrence &&
        recurrenceRule !== 'none' &&
        showRecurrenceRow}
    </FormProvider>
  );
};

export default RecurrencePatternBuilder;
